import React, { useEffect, useMemo, useRef, useState } from "react";
import DOMPurify from "dompurify";
import Message from "../Message";
import Button from "../Button";
import Reveal from "../Reveal";
import Terms from "../Terms";
import MemoryBoard from "./MemoryBoard";
import MemoryResult from "./MemoryResult";
import "./MemoryGameContainer.scss";

const shuffle = (items) => {
  const arr = [...items];
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
};

const buildMemoryCards = (pairs) =>
  shuffle(
    pairs.flatMap((pair, index) => {
      const pairId = pair.id || `pair-${index + 1}`;
      return [
        { id: `${pairId}-a`, pairId, imageUrl: pair.imageUrl || "" },
        { id: `${pairId}-b`, pairId, imageUrl: pair.imageUrl || "" },
      ];
    })
  );

const getCardOrderSignature = (cards) => cards.map((card) => card.id).join("|");

const buildReshuffledCards = (pairs, currentCards) => {
  const currentSignature = getCardOrderSignature(currentCards || []);
  let nextCards = buildMemoryCards(pairs);
  let tries = 0;
  while (
    currentSignature &&
    currentSignature === getCardOrderSignature(nextCards) &&
    tries < 5
  ) {
    nextCards = buildMemoryCards(pairs);
    tries += 1;
  }
  return nextCards;
};

const CONFETTI_PARTICLES = [
  { x: "-34vw", drift: "10vw", delay: "0ms", rotate: "-180deg" },
  { x: "-28vw", drift: "6vw", delay: "80ms", rotate: "220deg" },
  { x: "-22vw", drift: "8vw", delay: "140ms", rotate: "-240deg" },
  { x: "-16vw", drift: "5vw", delay: "40ms", rotate: "170deg" },
  { x: "-10vw", drift: "7vw", delay: "200ms", rotate: "-200deg" },
  { x: "-4vw", drift: "4vw", delay: "120ms", rotate: "260deg" },
  { x: "4vw", drift: "-4vw", delay: "20ms", rotate: "-260deg" },
  { x: "10vw", drift: "-7vw", delay: "170ms", rotate: "210deg" },
  { x: "16vw", drift: "-5vw", delay: "60ms", rotate: "-190deg" },
  { x: "22vw", drift: "-8vw", delay: "110ms", rotate: "240deg" },
  { x: "28vw", drift: "-6vw", delay: "180ms", rotate: "-230deg" },
  { x: "34vw", drift: "-10vw", delay: "90ms", rotate: "200deg" },
];

const normalizeMemoryConfig = (game) => {
  const pairsRaw = Array.isArray(game?.gameData?.pairs)
    ? game.gameData.pairs
    : [];
  const pairs = pairsRaw
    .map((pair, index) => ({
      id: pair?.id || `pair-${index + 1}`,
      imageUrl: String(pair?.imageUrl || "").trim(),
      description: String(pair?.description || pair?.message || "").trim(),
    }))
    .filter((pair) => pair.imageUrl);
  const settings = game?.gameData?.settings || {};
  const pairCount = Math.min(
    15,
    Math.max(1, Number(settings.pairCount || pairs.length || 1))
  );
  const limitedPairs = pairs.slice(0, pairCount);
  return {
    pairs: limitedPairs,
    cardBackImageUrl: settings.cardBackImageUrl || "",
    timerSeconds:
      settings.timerSeconds === null || settings.timerSeconds === undefined
        ? null
        : Math.max(0, Number(settings.timerSeconds)),
    showMatchSuccessPopup: Boolean(settings.showMatchSuccessPopup),
    memoryMatchSuccessMessage: String(
      settings.memoryMatchSuccessMessage || ""
    ).trim(),
  };
};

const MemoryGameContainer = ({
  game,
  client,
  textColor,
  backgroundColor,
  buttonColor,
}) => {
  const memoryConfig = useMemo(() => normalizeMemoryConfig(game), [game]);
  const [started, setStarted] = useState(false);
  const [cards, setCards] = useState(() =>
    buildMemoryCards(memoryConfig.pairs)
  );
  const [flippedCardIds, setFlippedCardIds] = useState([]);
  const [matchedPairIds, setMatchedPairIds] = useState([]);
  const [inputLocked, setInputLocked] = useState(false);
  const [timeLeft, setTimeLeft] = useState(memoryConfig.timerSeconds);
  const [timedOut, setTimedOut] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [message, setMessage] = useState(
    "Match pairs of cards by remembering where images are."
  );
  const [selectedDescription, setSelectedDescription] = useState("");
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [successPopupText, setSuccessPopupText] = useState("");
  const [successPopupKey, setSuccessPopupKey] = useState(0);
  const successPopupTimeoutRef = useRef(null);
  const isComplete =
    memoryConfig.pairs.length > 0 &&
    matchedPairIds.length >= memoryConfig.pairs.length;

  useEffect(() => {
    setCards(buildMemoryCards(memoryConfig.pairs));
    setFlippedCardIds([]);
    setMatchedPairIds([]);
    setInputLocked(false);
    setTimeLeft(memoryConfig.timerSeconds);
    setTimedOut(false);
    setStarted(false);
    setShowTerms(false);
    setMessage("Match pairs of cards by remembering where images are.");
    setSelectedDescription("");
    setShowSuccessPopup(false);
    setSuccessPopupText("");
    setSuccessPopupKey(0);
  }, [memoryConfig]);

  useEffect(
    () => () => {
      if (successPopupTimeoutRef.current) {
        window.clearTimeout(successPopupTimeoutRef.current);
      }
    },
    []
  );

  useEffect(() => {
    if (
      !started ||
      timedOut ||
      isComplete ||
      showSuccessPopup ||
      memoryConfig.timerSeconds === null
    ) {
      return undefined;
    }
    if (timeLeft <= 0) {
      setTimedOut(true);
      return undefined;
    }

    const intervalId = setInterval(() => {
      setTimeLeft((prev) => (prev === null ? prev : prev - 1));
    }, 1000);

    return () => clearInterval(intervalId);
  }, [
    started,
    timedOut,
    isComplete,
    showSuccessPopup,
    timeLeft,
    memoryConfig.timerSeconds,
  ]);

  useEffect(() => {
    if (timedOut) {
      setInputLocked(true);
    }
  }, [timedOut]);

  const handleStart = () => {
    setStarted(true);
    setMessage(
      game?.introMessage?.instructions || "Find all matching image pairs."
    );
  };

  const restartGame = () => {
    setCards((prev) => buildReshuffledCards(memoryConfig.pairs, prev));
    setFlippedCardIds([]);
    setMatchedPairIds([]);
    setInputLocked(false);
    setTimedOut(false);
    setTimeLeft(memoryConfig.timerSeconds);
    setStarted(true);
    setMessage(
      game?.introMessage?.instructions || "Find all matching image pairs."
    );
    setSelectedDescription("");
    setShowSuccessPopup(false);
    setSuccessPopupText("");
  };

  const triggerSuccessPopup = (pairDescription) => {
    if (!memoryConfig.showMatchSuccessPopup) return;

    if (successPopupTimeoutRef.current) {
      window.clearTimeout(successPopupTimeoutRef.current);
    }

    const lines = [
      memoryConfig.memoryMatchSuccessMessage,
      pairDescription,
    ].filter((line) => String(line || "").trim());
    setSuccessPopupText(lines.join("\n\n") || "Great match!");
    setSuccessPopupKey((prev) => prev + 1);
    setShowSuccessPopup(true);

    successPopupTimeoutRef.current = window.setTimeout(() => {
      setShowSuccessPopup(false);
      setSuccessPopupText("");
    }, 2800);
  };

  const handleCardClick = (card) => {
    if (!started || inputLocked || showSuccessPopup || timedOut || isComplete)
      return;
    if (flippedCardIds.includes(card.id)) return;
    if (matchedPairIds.includes(card.pairId)) return;

    const clickedPair = memoryConfig.pairs.find(
      (pair) => pair.id === card.pairId
    );
    setSelectedDescription(clickedPair?.description || "");

    const nextFlipped = [...flippedCardIds, card.id];
    setFlippedCardIds(nextFlipped);

    if (nextFlipped.length < 2) {
      return;
    }

    setInputLocked(true);
    const [firstId, secondId] = nextFlipped;
    const first = cards.find((entry) => entry.id === firstId);
    const second = cards.find((entry) => entry.id === secondId);
    const isMatch = first?.pairId && first.pairId === second?.pairId;
    const matchedPair = isMatch
      ? memoryConfig.pairs.find((pair) => pair.id === first?.pairId)
      : null;

    window.setTimeout(() => {
      if (isMatch) {
        setMatchedPairIds((prev) => [...prev, first.pairId]);
        setMessage("Great match! Keep going.");
        triggerSuccessPopup(matchedPair?.description || "");
      } else {
        setMessage("Not a match. Try again.");
        setSelectedDescription("");
      }
      setFlippedCardIds([]);
      setInputLocked(false);
    }, 700);
  };

  if (showTerms) {
    return (
      <Terms
        setShowTerms={setShowTerms}
        termsContent={game?.terms}
        textColor={textColor}
        backgroundColor={backgroundColor}
        buttonColor={buttonColor}
      />
    );
  }

  if (timedOut) {
    return (
      <MemoryResult
        title="Time's up!"
        description="The timer has ended. Restart to try again."
        matchedCount={matchedPairIds.length}
        totalPairs={memoryConfig.pairs.length}
        buttonText="Restart"
        onButtonClick={restartGame}
        buttonColor={buttonColor}
        client={client}
      />
    );
  }

  if (
    isComplete &&
    game?.redirectButtonText &&
    (!memoryConfig.showMatchSuccessPopup || !showSuccessPopup)
  ) {
    return (
      <>
        <Message
          message={game?.successMessage || "Congratulations, you got it!"}
          textColor={textColor}
          from="memory"
        />
        <Reveal
          successMessage={game?.successMessage}
          redirectButtonText={game?.redirectButtonText}
          redirectButtonLink={game?.redirectButtonLink}
          redirectButtonColor={game?.redirectButtonColor}
          matchedCount={matchedPairIds.length}
          totalPairs={memoryConfig.pairs.length}
          client={client}
          from="memory"
        />
      </>
    );
  }

  return (
    <div
      className="MemoryGameContainer"
      style={{ marginTop: client === "airfrance" ? "10px" : 0 }}
    >
      {!started ? (
        <>
          <Message
            message={
              <span className="introMessage">
                {game?.bannerImage ? (
                  <img
                    src={game.bannerImage}
                    alt="Game Banner"
                    className="banner-image"
                  />
                ) : null}
                <h2
                  className="title"
                  style={{ backgroundColor: game?.buttonColor, color: "white" }}
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(game?.name || "Memory Game"),
                  }}
                />
                <div className="welcome-message">
                  {game?.introMessage?.instructions ||
                    "Find all matching image pairs."}
                </div>
                <div
                  className="instructions"
                  dangerouslySetInnerHTML={{
                    __html: game?.introMessage?.howTo || "",
                  }}
                />
              </span>
            }
            textColor={textColor}
          />
          <div className="memory-start-actions">
            <Button
              onClick={handleStart}
              style={{
                backgroundColor: buttonColor || undefined,
                color: "#fff",
              }}
            >
              Start
            </Button>
            {game?.terms?.trim() ? (
              <button
                type="button"
                className="memory-terms-link"
                onClick={() => setShowTerms(true)}
              >
                Terms & Conditions
              </button>
            ) : null}
          </div>
        </>
      ) : (
        <>
          {client === "airfrance" ? (
            <div className="airfrance-header">Premium Match Game</div>
          ) : null}
          <div className="memory-hud">
            {memoryConfig.timerSeconds !== null ? (
              <div
                className={`memory-countdown ${
                  Number(timeLeft || 0) <= 10 ? "is-warning" : ""
                }`.trim()}
              >
                {Math.max(0, Number(timeLeft || 0))}s
              </div>
            ) : null}
            <div>{`Pairs Matched: ${matchedPairIds.length}/${memoryConfig.pairs.length}`}</div>
          </div>

          <Message message={message} textColor={textColor} />

          <MemoryBoard
            cards={cards}
            flippedCardIds={flippedCardIds}
            matchedPairIds={matchedPairIds}
            inputLocked={inputLocked}
            onCardClick={handleCardClick}
            cardBackImageUrl={memoryConfig.cardBackImageUrl}
            cardBackgroundColor={game?.cardBackgroundColor}
          />

          <div className="memory-match-description">{selectedDescription}</div>
          {showSuccessPopup ? (
            <div className="memory-success-popup-overlay">
              <div className="memory-confetti-layer" aria-hidden="true">
                {CONFETTI_PARTICLES.map((particle, index) => (
                  <span
                    key={`confetti-${successPopupKey}-${index}`}
                    className="memory-confetti-piece"
                    style={{
                      "--confetti-x": particle.x,
                      "--confetti-drift": particle.drift,
                      "--confetti-delay": particle.delay,
                      "--confetti-rotate": particle.rotate,
                    }}
                  />
                ))}
              </div>
              <div
                key={successPopupKey}
                className="memory-success-popup"
                role="status"
                aria-live="polite"
              >
                {successPopupText}
              </div>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
};

export default MemoryGameContainer;
